/**
 * HelpAssistant — composants UI de l'assistant d'aide IA (Help Drawer).
 *
 * Identité visuelle IA = token `--ai` (violet, couleur Tailwind `ai`) : étoiles
 * `Sparkles`, liserés/fonds violets sur la barre de question et les réponses
 * (fonds dérivés par opacité `bg-ai/10`…). Les cartes d'articles sources restent
 * NEUTRES (vrais articles, pas du texte généré).
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronRight, Loader2, RotateCcw, Send, Sparkles } from 'lucide-react'
import { cn } from '../../lib/utils'
import { Input } from '../ui/input'
import { Button } from '../ui/button'
import { MarkdownRenderer } from './MarkdownRendererLazy'
import type { AssistantSource, AssistantTurn } from '../../hooks/useHelpAssistant'

// ── Champ de saisie (barre IA + relance) ─────────────────────────────────

function AssistantInput({
    placeholder,
    disabled,
    onSubmit,
}: {
    placeholder: string
    disabled?: boolean
    onSubmit: (value: string) => void
}) {
    const [value, setValue] = useState('')

    const submit = () => {
        const v = value.trim()
        if (!v || disabled) return
        onSubmit(v)
        setValue('')
    }

    return (
        <div className="relative">
            <Sparkles className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ai" />
            <Input
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        submit()
                    }
                }}
                placeholder={placeholder}
                disabled={disabled}
                className="h-11 rounded-xl border-ai/30 bg-ai/10 pl-9 pr-11 focus-visible:ring-ai/50"
            />
            <button
                type="button"
                onClick={submit}
                disabled={disabled || !value.trim()}
                aria-label={placeholder}
                className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-lg text-ai transition-colors hover:bg-ai/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai/50 disabled:opacity-40 motion-reduce:transition-none"
            >
                <Send className="h-4 w-4" />
            </button>
        </div>
    )
}

// ── Fil de discussion ────────────────────────────────────────────────────

function UserBubble({ content }: { content: string }) {
    return (
        <div className="rounded-xl bg-muted px-3 py-2 text-sm font-medium text-foreground">
            {content}
        </div>
    )
}

/** Carte d'article source. La principale est mise en avant de façon NEUTRE
 *  (élévation + liseré gris + titre plus gras) — pas de couleur de marque (vert)
 *  qui jurerait avec le panneau violet, ni de violet (réservé au texte IA). */
function SourceCard({
    source,
    primary,
    onSelect,
}: {
    source: AssistantSource
    primary?: boolean
    onSelect: (slug: string, section?: string | null) => void
}) {
    return (
        <button
            type="button"
            onClick={() => onSelect(source.slug, source.section)}
            className={cn(
                'flex w-full items-center justify-between gap-2 rounded-lg bg-card px-3 py-2 text-left text-sm transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none',
                primary
                    ? 'border border-foreground/15 shadow-md hover:shadow-lg'
                    : 'shadow-sm hover:shadow',
            )}
        >
            <span className="min-w-0">
                <span
                    className={cn(
                        'block truncate text-foreground',
                        primary ? 'font-semibold' : 'font-medium',
                    )}
                >
                    {source.title}
                </span>
                {source.section && (
                    <span className="block truncate text-xs text-muted-foreground">
                        › {source.section}
                    </span>
                )}
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
    )
}

function SourceList({
    sources,
    onSelectArticle,
}: {
    sources: AssistantSource[]
    onSelectArticle: (slug: string, section?: string | null) => void
}) {
    const { t } = useTranslation()
    const primary = sources.find((s) => s.primary) ?? sources[0]
    const others = sources.filter((s) => s !== primary)
    return (
        <div className="mt-3 space-y-3">
            <div>
                <p className="mb-1.5 text-xs font-semibold text-foreground">
                    {t('help.assistant.primarySource')}
                </p>
                <SourceCard source={primary} primary onSelect={onSelectArticle} />
            </div>
            {others.length > 0 && (
                <div>
                    <p className="mb-1.5 text-xs font-medium text-muted-foreground">
                        {t('help.assistant.sourcesLabel')}
                    </p>
                    <ul className="space-y-1.5">
                        {others.map((s) => (
                            <li key={s.slug}>
                                <SourceCard source={s} onSelect={onSelectArticle} />
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    )
}

function AssistantBubble({
    turn,
    isStreaming,
    onSelectArticle,
}: {
    turn: AssistantTurn
    isStreaming: boolean
    onSelectArticle: (slug: string, section?: string | null) => void
}) {
    const { t } = useTranslation()
    return (
        <div className="rounded-xl border border-ai/30 bg-ai/10 p-3">
            <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-ai">
                <Sparkles className="h-3.5 w-3.5" />
                {t('help.assistant.title')}
            </div>
            {/* aria-live : le flux de tokens est annoncé aux lecteurs d'écran (§10). */}
            <div aria-live="polite" aria-atomic="false">
                {turn.content ? (
                    <MarkdownRenderer
                        content={turn.content}
                        className="prose-sm prose-headings:text-foreground prose-p:text-foreground"
                    />
                ) : (
                    isStreaming && (
                        <div className="flex items-center gap-2 text-sm text-ai" role="status">
                            <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" />
                            {t('help.assistant.thinking')}
                        </div>
                    )
                )}
            </div>
            {turn.sources && turn.sources.length > 0 && (
                <SourceList sources={turn.sources} onSelectArticle={onSelectArticle} />
            )}
        </div>
    )
}

/** Vue conversation : prend tout le corps du drawer dès qu'une question est posée. */
export function AssistantConversation({
    messages,
    isStreaming,
    error,
    atLimit,
    onAsk,
    onReset,
    onSelectArticle,
}: {
    messages: AssistantTurn[]
    isStreaming: boolean
    error: string | null
    atLimit: boolean
    onAsk: (value: string) => void
    onReset: () => void
    onSelectArticle: (slug: string, section?: string | null) => void
}) {
    const { t } = useTranslation()
    const hasMessages = messages.length > 0
    return (
        <div className="space-y-3">
            {messages.map((m, i) =>
                m.role === 'user' ? (
                    <UserBubble key={i} content={m.content} />
                ) : (
                    <AssistantBubble
                        key={i}
                        turn={m}
                        isStreaming={isStreaming && i === messages.length - 1}
                        onSelectArticle={onSelectArticle}
                    />
                ),
            )}
            {error && <p className="text-sm text-destructive">{t('help.assistant.error')}</p>}
            {atLimit ? (
                <Button
                    variant="outline"
                    onClick={onReset}
                    className="w-full gap-2 rounded-xl border-ai/30 text-ai hover:bg-ai/10 hover:text-ai"
                >
                    <RotateCcw className="h-4 w-4" />
                    {t('help.assistant.newConversation')}
                </Button>
            ) : (
                // Placeholder explicite : « poser une question » sur un fil vierge
                // (nouvelle conversation), « question complémentaire » sur un suivi.
                <AssistantInput
                    placeholder={
                        hasMessages
                            ? t('help.assistant.followUpPlaceholder')
                            : t('help.assistant.askPlaceholder')
                    }
                    disabled={isStreaming}
                    onSubmit={onAsk}
                />
            )}
            {/* Mise en garde bêta discrète (style « l'IA peut se tromper »). */}
            <p className="px-1 text-center text-[11px] leading-snug text-muted-foreground">
                {t('help.assistant.betaHint')}
            </p>
        </div>
    )
}
